import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/Icon";

const API_ICONS = { rest: "Globe", graphql: "GitBranch" };

export default function CommunityIntegrationCard({ integration, onEdit, onConnect, onDisconnect, onDelete }) {
    const { t } = useTranslation();
    const endpointCount = Array.isArray(integration.endpoints) ? integration.endpoints.length : 0;
    const errorCount = Array.isArray(integration.errorMap) ? integration.errorMap.length : 0;
    const authType = integration.auth?.type || "none";
    const isPrivate = integration.visibility === "private";
    const isDraft = integration.status === "draft";
    const isConnected = !!integration.isConnected;
    // Server-computed flag (see getCommunityIntegrations.js). The raw
    // createdById is intentionally not returned to the client, so we trust
    // the server's per-row owner determination.
    const isCreator = !!integration.isCreator;
    const createdAt = integration.createdAt ? new Date(integration.createdAt).toLocaleDateString() : "";

    return (
        <div className={`bg-white border rounded-lg p-5 hover:shadow-md transition-shadow group ${isConnected ? "border-green-200" : ""}`}>
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                    {integration.logoUrl ? (
                        <img src={integration.logoUrl} alt="" className="w-10 h-10 rounded-lg object-contain bg-gray-50" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                    ) : null}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isConnected ? "bg-gradient-to-br from-green-100 to-emerald-100" : "bg-gradient-to-br from-blue-100 to-purple-100"}`} style={integration.logoUrl ? { display: "none" } : {}}>
                        <Icon name={API_ICONS[integration.apiType] || "Globe"} className={`w-6 h-6 ${isConnected ? "text-green-600" : "text-blue-600"}`} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                        <p className="text-xs text-gray-500 font-mono">{integration.baseUrl}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    {isDraft && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full font-medium">
                            {t("common.draft")}
                        </span>
                    )}
                    {isConnected && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium flex items-center gap-1">
                            <Icon name="Check" className="w-3 h-3" />
                            {t("integrations.community.connected")}
                        </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${isPrivate ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                        {isPrivate ? t("common.private") : t("common.public")}
                    </span>
                </div>
            </div>
            {integration.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{integration.description}</p>}
            <div className="flex flex-wrap gap-2 mb-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Icon name="Layers" className="w-3 h-3" /> {endpointCount} {t("communityIntegrations.card.endpoints")}</span>
                <span className="flex items-center gap-1"><Icon name="Shield" className="w-3 h-3" /> {errorCount} {t("communityIntegrations.card.errors")}</span>
                <span className="flex items-center gap-1"><Icon name="Key" className="w-3 h-3" /> {authType}</span>
                <span className="uppercase font-medium text-gray-400">{integration.apiType}</span>
            </div>
            <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{createdAt}{integration.createdBy?.firstName ? ` ${t("common.by")} ${integration.createdBy.firstName}` : ""}</span>
                <div className="flex items-center gap-2">
                    {isDraft && isCreator && (
                        <button onClick={() => onEdit(integration)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1">
                            <Icon name="Play" className="w-3.5 h-3.5" />
                            {t("common.resume")}
                        </button>
                    )}
                    {!isDraft && !isConnected && (
                        <button onClick={() => onConnect?.(integration)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1">
                            <Icon name="Plug" className="w-3.5 h-3.5" />
                            {t("integrations.community.connect")}
                        </button>
                    )}
                    {!isDraft && isConnected && (
                        <button onClick={() => onDisconnect?.(integration)} className="px-3 py-1.5 bg-gray-100 text-red-600 text-sm rounded-md hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-1">
                            <Icon name="Unplug" className="w-3.5 h-3.5" />
                            {t("integrations.community.disconnect")}
                        </button>
                    )}
                    {!isDraft && isCreator && (
                        <button onClick={() => onEdit(integration)} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-1">
                            <Icon name="Settings" className="w-3.5 h-3.5" />
                            {t("common.edit")}
                        </button>
                    )}
                    {isCreator && (
                        <button onClick={() => onDelete?.(integration)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title={t("common.delete")}>
                            <Icon name="Trash2" className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
